import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Award, Star, Calendar, Users, CheckCircle, ArrowRight,
  Sparkles, Target, TrendingUp, Clock, Share2, Mail, ArrowLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seoConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AwardsLandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasNomination, setHasNomination] = useState(false);

  useEffect(() => {
    fetchAwardConfig();
    if (isAuthenticated) {
      checkNominationStatus();
    }
  }, [isAuthenticated]);

  const fetchAwardConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/config`);
      const data = await response.json();
      console.log('Awards config fetched:', data);
      if (data.success) {
        setConfig(data.config);
        console.log('Config status:', data.config?.status);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkNominationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/awards/my-nomination`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      console.log('Nomination status:', data);
      setHasNomination(data.has_nomination);
    } catch (error) {
      console.error('Error checking nomination:', error);
      // If error, assume no nomination
      setHasNomination(false);
    }
  };

  const handleNominate = () => {
    console.log('Nominate button clicked', { isAuthenticated, user });
    
    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please login with LinkedIn to nominate yourself',
        variant: 'destructive'
      });
      console.log('Redirecting to login...');
      navigate('/login');
      return;
    }
    
    console.log('User is authenticated, navigating to nomination form...');
    navigate('/awards/nominate');
  };

  const categories = [
    {
      key: 'rookie',
      name: 'Best TA Rookie Award',
      experience: '6 months – 2 years',
      winners: 10,
      description: 'Rising stars who show promise, energy, and creativity in the early phase of their TA journey.',
      icon: Sparkles,
      gradient: 'from-green-400 to-emerald-600',
      color: 'text-green-600'
    },
    {
      key: 'emerging_leader',
      name: 'Best Emerging TA Leader Award',
      experience: '4 – 8 years',
      winners: 10,
      description: 'Professionals who are shaping the future with innovative sourcing and engagement strategies.',
      icon: TrendingUp,
      gradient: 'from-blue-400 to-cyan-600',
      color: 'text-blue-600'
    },
    {
      key: 'excellence',
      name: 'Best TA Excellence Award',
      experience: '2 – 4 years',
      winners: 10,
      description: 'Established recruiters driving measurable impact and excellence across the recruitment lifecycle.',
      icon: Target,
      gradient: 'from-purple-400 to-indigo-600',
      color: 'text-purple-600'
    },
    {
      key: 'manager',
      name: 'Best TA Manager Award',
      experience: '8 – 12 years',
      winners: 10,
      description: 'Strategic leaders managing teams and delivering consistent talent success across domains.',
      icon: Users,
      gradient: 'from-orange-400 to-red-600',
      color: 'text-orange-600'
    },
    {
      key: 'senior_leader',
      name: 'Best TA Senior Leader Award',
      experience: '12+ years',
      winners: 5,
      description: 'Visionary TA professionals who have redefined recruitment leadership at scale.',
      icon: Award,
      gradient: 'from-yellow-400 to-amber-600',
      color: 'text-yellow-600'
    }
  ];

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const timeline = [
    {
      phase: 'Nominations Open',
      dates: config ? `${fmtDate(config.nomination_start_date)} – ${fmtDate(config.nomination_end_date)}` : 'Jun 1 – Sep 30, 2026',
      description: 'TA professionals submit their entries through the online nomination form.',
      icon: Calendar,
      status: config?.nominations_open ? 'active' : 'upcoming'
    },
    {
      phase: 'Voting Period',
      dates: config ? `${fmtDate(config.voting_start_date)} – ${fmtDate(config.voting_end_date)}` : 'Oct 1 – Nov 15, 2026',
      description: 'Public and professional voting through LinkedIn authentication.',
      icon: Users,
      status: config?.voting_open ? 'active' : 'upcoming'
    },
    {
      phase: 'Review & Validation',
      dates: config ? `${fmtDate(config.voting_end_date)} – ${fmtDate(config.announcement_date)}` : 'Nov 16 – Nov 30, 2026',
      description: 'RecruiterIQ team validates all votes and profiles for authenticity.',
      icon: CheckCircle,
      status: config?.status === 'review' ? 'active' : 'upcoming'
    },
    {
      phase: 'Results Announcement',
      dates: config ? fmtDate(config.announcement_date) : 'Dec 1, 2026',
      description: 'Winners announced on the RecruiterIQ platform and social media channels.',
      icon: Trophy,
      status: config?.status === 'completed' ? 'active' : 'upcoming'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={seoConfig.awards.title}
        description={seoConfig.awards.description}
        keywords={seoConfig.awards.keywords}
        canonical={seoConfig.awards.canonical}
        schema={{
          "@type": "Event",
          "name": `TA Awards ${config?.year || 2026}`,
          "description": seoConfig.awards.description,
          "startDate": config?.nomination_start_date || "2026-06-01",
          "endDate": config?.announcement_date || "2026-12-01",
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode"
        }}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-300 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          {/* Back to Home Button */}
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-6 bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <Trophy className="w-5 h-5 text-yellow-300" />
              <span className="font-semibold">RecruiterIQ Global TA Awards {config?.year || '2026'}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Recognizing Excellence in<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300">
                Talent Acquisition
              </span>
            </h1>
            
            <p className="text-base md:text-xl text-purple-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              The RecruiterIQ Global TA Awards celebrate the best and brightest in the Talent Acquisition community — 
              professionals who are transforming how organizations hire, connect, and build teams.
            </p>

            {/* Always show nomination buttons if not already nominated */}
            {!hasNomination && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center relative z-10">
                <button
                  onClick={handleNominate}
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-900 font-bold text-lg px-8 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all flex items-center gap-2"
                >
                  <Award className="w-6 h-6" />
                  Nominate Yourself Now
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    const categoriesSection = document.getElementById('categories');
                    if (categoriesSection) {
                      categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="border-2 border-white text-white hover:bg-white/20 font-semibold px-8 py-6 rounded-full transition-all flex items-center gap-2"
                >
                  Explore Categories
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}

            {hasNomination && (
              <div className="flex gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/awards/dashboard')}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-6 rounded-full shadow-2xl transition-all flex items-center gap-2"
                >
                  <Star className="w-6 h-6" />
                  View My Dashboard
                </button>
              </div>
            )}

            {config?.voting_open && (
              <Badge className="mt-6 bg-green-500 text-white px-6 py-2 text-lg">
                🗳️ Voting is LIVE! Support your favorite TA professionals
              </Badge>
            )}
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="rgb(248, 250, 252)"/>
          </svg>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Award Timeline</h2>
          <p className="text-xl text-slate-600">Your journey to recognition</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {timeline.map((item, idx) => {
            const Icon = item.icon;
            return (
              <Card 
                key={idx}
                className={`relative overflow-hidden transition-all hover:shadow-xl ${
                  item.status === 'active' ? 'border-2 border-purple-500 shadow-lg' : ''
                }`}
              >
                {item.status === 'active' && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
                    ACTIVE
                  </div>
                )}
                <CardContent className="pt-6">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center mb-4 ${
                    item.status === 'active' ? 'animate-pulse' : ''
                  }`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.phase}</h3>
                  <p className="text-purple-600 font-semibold mb-2">{item.dates}</p>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Categories Section */}
      <div id="categories" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Award Categories</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Five levels of excellence, celebrating TA professionals at every stage of their career journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Card 
                  key={category.key}
                  className="relative overflow-hidden group hover:shadow-2xl transition-all duration-300 border-2 hover:border-purple-300"
                >
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <Badge className="bg-purple-100 text-purple-700 font-bold">
                        Top {category.winners}
                      </Badge>
                    </div>
                    <CardTitle className="text-2xl text-slate-900 group-hover:text-purple-600 transition-colors">
                      {category.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-5 h-5" />
                        <span className="font-semibold">{category.experience}</span>
                      </div>
                      <p className="text-slate-600 leading-relaxed">
                        {category.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Qualification Requirements */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Qualification Criteria</h2>
            <p className="text-xl text-slate-600">To qualify, gather 23 verified votes via LinkedIn authentication</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { type: 'Candidates', count: 10, icon: '👥', color: 'from-blue-400 to-blue-600' },
              { type: 'Industry Peers', count: 5, icon: '🤝', color: 'from-green-400 to-green-600' },
              { type: 'Seniors/Juniors', count: 5, icon: '👔', color: 'from-purple-400 to-purple-600' },
              { type: 'Company Managers', count: 2, icon: '💼', color: 'from-orange-400 to-orange-600' },
              { type: 'HR Team', count: 1, icon: '🎯', color: 'from-pink-400 to-pink-600' }
            ].map((item, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-all">
                <CardContent className="pt-6">
                  <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center text-4xl mb-4 shadow-lg`}>
                    {item.icon}
                  </div>
                  <div className="text-4xl font-bold text-slate-900 mb-2">{item.count}</div>
                  <p className="text-sm font-semibold text-slate-600">{item.type}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Badge className="bg-purple-600 text-white px-6 py-3 text-lg">
              <CheckCircle className="w-5 h-5 inline mr-2" />
              Total: 23 Verified Votes Required
            </Badge>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <Trophy className="w-20 h-20 mx-auto mb-6 text-yellow-300" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Showcase Your Excellence?
          </h2>
          <p className="text-xl text-purple-100 mb-8 leading-relaxed">
            "The TA Awards are not just about recognition — they're about celebrating the heart of recruitment.
            Every conversation, every placement, every person you've helped shape their future — this is your story.
            Let's celebrate the people who make hiring human again."
          </p>

          {config?.nominations_open && !hasNomination && (
            <Button
              onClick={handleNominate}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-purple-900 font-bold text-xl px-12 py-7 rounded-full shadow-2xl transform hover:scale-105 transition-all"
            >
              <Star className="w-6 h-6 mr-2" />
              Start Your Nomination
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          )}

          {config?.voting_open && (
            <div className="space-y-4">
              <Button
                size="lg"
                className="bg-white text-purple-600 hover:bg-purple-50 font-bold text-xl px-12 py-7 rounded-full shadow-2xl"
              >
                <Users className="w-6 h-6 mr-2" />
                Vote for Nominees
              </Button>
              <p className="text-purple-200">Support exceptional TA professionals in their journey to recognition</p>
            </div>
          )}
        </div>
      </div>

      {/* Past Winners Link */}
      {config?.year && config.year > 2026 && (
        <div className="bg-slate-100 py-12">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Previous Winners</h3>
            <Button
              onClick={() => navigate(`/awards/winners/${config.year - 1}`)}
              variant="outline"
              className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              View {config.year - 1} Winners
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AwardsLandingPage;
